import { autoPlace } from "@founderdeck/core";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { doSync } from "../lib/sync";
import { setSyncRunner } from "../lib/syncController";
import { Overlays } from "../components/overlays";
import type { NoteInput, Task, TaskInput } from "../lib/api";
import { todayKey } from "../lib/date";
import {
  useDayLogMutations,
  useDayLogs,
  useNoteMutations,
  useNotes,
  useProjectMutations,
  useProjects,
  useTaskMutations,
  useTasks,
} from "../lib/queries";
import { ding, pip } from "../lib/sound";
import { isStale, projectMap } from "../lib/tasks";
import { AppContext, type AppApi, type TimerApi } from "./AppContext";

interface TimerMachine {
  mode: "work" | "break";
  left: number;
  running: boolean;
  workMin: number;
  brkMin: number;
  linkedTaskId: string | null;
  worked: number; // accumulated work seconds, for actualMin crediting
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tasksQ = useTasks();
  const projectsQ = useProjects();
  const dayLogsQ = useDayLogs();
  const notesQ = useNotes();
  const taskMut = useTaskMutations();
  const projectMut = useProjectMutations();
  const dayLogMut = useDayLogMutations();
  const noteMut = useNoteMutations();

  const tasks = useMemo(() => tasksQ.data ?? [], [tasksQ.data]);
  const projects = useMemo(() => projectsQ.data ?? [], [projectsQ.data]);
  const dayLogs = useMemo(() => dayLogsQ.data ?? [], [dayLogsQ.data]);
  const notes = useMemo(() => notesQ.data ?? [], [notesQ.data]);
  const pmap = useMemo(() => projectMap(projects), [projects]);
  const today = todayKey();
  const todayLog = dayLogs.find((d) => d.date === today);

  // Keep the latest tasks reachable from imperative callbacks (timer).
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const [toastState, setToastState] = useState<{ message: string; undo?: () => void } | null>(
    null,
  );
  const toastTimer = useRef<number | null>(null);
  const toast = useCallback((message: string, undo?: () => void) => {
    setToastState({ message, undo });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastState(null), 4200);
  }, []);

  // UI overlays
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [plan, setPlan] = useState(false);
  const [shutdown, setShutdown] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [focusOpen, setFocusOpen] = useState(false);
  const [taskModal, setTaskModal] = useState<{ id: string | null } | null>(null);
  const openTask = useCallback((id: string | null = null) => setTaskModal({ id }), []);
  const [filter, setFilter] = useState("all");
  // If the filtered project is deleted (here or on another device), fall back to All.
  useEffect(() => {
    if (filter !== "all" && !projects.some((p) => p.id === filter)) setFilter("all");
  }, [filter, projects]);

  // ---- task actions ----
  const createTask = useCallback(
    (input: TaskInput & { title: string }): string => {
      pip();
      const id = crypto.randomUUID();
      taskMut.create.mutate({ ...input, id });
      return id;
    },
    [taskMut.create],
  );
  const updateTask = useCallback(
    (id: string, patch: TaskInput) => taskMut.update.mutate({ id, patch }),
    [taskMut.update],
  );
  const deleteTask = useCallback(
    (t: Task) => {
      taskMut.remove.mutate(t.id);
      toast(`Deleted "${t.title.slice(0, 32)}"`);
    },
    [taskMut.remove, toast],
  );
  const toggleDone = useCallback(
    (t: Task) => {
      const done = t.status !== "done";
      if (done) ding();
      updateTask(t.id, { status: done ? "done" : "todo" });
    },
    [updateTask],
  );
  const toggleMit = useCallback(
    (t: Task) => {
      // Top-3 is per-day: a star belongs to the task's planned day (or today).
      const day = t.mitDate ?? t.doDate ?? todayKey();
      const starred = t.mitDate != null;
      if (
        !starred &&
        tasksRef.current.filter((x) => x.mitDate === day && x.status !== "done").length >= 3
      ) {
        toast("Top 3 is full for that day — pick, not pile.");
        return;
      }
      updateTask(t.id, starred ? { mit: false, mitDate: null } : { mit: true, mitDate: day });
    },
    [updateTask, toast],
  );
  const addProject = useCallback(
    (name: string) => projectMut.create.mutate({ name }),
    [projectMut.create],
  );

  const createNote = useCallback(
    (input: NoteInput = {}): string => {
      const id = crypto.randomUUID();
      noteMut.create.mutate({ ...input, id });
      return id;
    },
    [noteMut.create],
  );
  const updateNote = useCallback(
    (id: string, patch: NoteInput) => noteMut.update.mutate({ id, patch }),
    [noteMut.update],
  );
  const deleteNote = useCallback(
    (id: string) => noteMut.remove.mutate(id),
    [noteMut.remove],
  );

  const autoPlaceToday = useCallback(() => {
    const open = tasksRef.current.filter((t) => t.doDate === today && t.status !== "done");
    const placements = autoPlace(
      open.map((t) => ({
        id: t.id,
        estMin: t.estMin,
        energy: t.energy,
        fromHour: t.fromHour,
        pri: t.pri,
      })),
    );
    for (const [id, fromHour] of Object.entries(placements)) {
      updateTask(id, { fromHour, doDate: today });
    }
    toast("Auto-placed today's tasks.");
  }, [today, updateTask, toast]);

  const rollStale = useCallback(() => {
    const stale = tasksRef.current.filter(isStale);
    for (const t of stale) updateTask(t.id, { doDate: today, fromHour: null });
    toast(`Rolled ${stale.length} stale → today.`);
  }, [today, updateTask, toast]);

  // ---- rituals ----
  const planDay = useCallback(
    (pickedIds: Set<string>, auto: boolean) => {
      for (const t of tasksRef.current) {
        if (t.status === "done") continue;
        if (pickedIds.has(t.id)) {
          if (t.doDate !== today) updateTask(t.id, { doDate: today, fromHour: null });
        } else if (t.doDate === today) {
          updateTask(t.id, { doDate: null, fromHour: null });
        }
      }
      dayLogMut.upsert.mutate({ date: today, patch: { planned: true } });
      if (auto) setTimeout(() => autoPlaceToday(), 300);
      ding();
      toast("Day planned. Protect the blocks.");
    },
    [today, updateTask, dayLogMut.upsert, autoPlaceToday, toast],
  );

  const completeShutdown = useCallback(() => {
    dayLogMut.upsert.mutate({ date: today, patch: { shutdown: true } });
    ding();
    toast("Shutdown complete. Go be a person.");
  }, [today, dayLogMut.upsert, toast]);

  // ---- timer machine ----
  const m = useRef<TimerMachine>({
    mode: "work",
    left: 25 * 60,
    running: false,
    workMin: 25,
    brkMin: 5,
    linkedTaskId: null,
    worked: 0,
  });
  const [, force] = useReducer((x: number) => x + 1, 0);
  const ivRef = useRef<number | null>(null);

  const creditActual = useCallback(() => {
    const s = m.current;
    if (s.worked >= 60 && s.linkedTaskId) {
      const t = tasksRef.current.find((x) => x.id === s.linkedTaskId);
      if (t) {
        updateTask(t.id, { actualMin: (t.actualMin ?? 0) + Math.round(s.worked / 60) });
      }
    }
    s.worked = 0;
  }, [updateTask]);

  const dayLogUpsertRef = useRef(dayLogMut.upsert.mutate);
  dayLogUpsertRef.current = dayLogMut.upsert.mutate;
  const creditRef = useRef(creditActual);
  creditRef.current = creditActual;

  const tick = useCallback(() => {
    const s = m.current;
    if (!s.running) return;
    if (s.mode === "work") s.worked++;
    if (s.left > 1) {
      s.left--;
      force();
      return;
    }
    if (s.mode === "work") {
      dayLogUpsertRef.current({ date: today, patch: { incPom: true } });
      creditRef.current();
      ding();
      s.mode = "break";
      s.left = s.brkMin * 60;
    } else {
      ding();
      s.mode = "work";
      s.left = s.workMin * 60;
    }
    force();
  }, [today]);

  const clearIv = useCallback(() => {
    if (ivRef.current != null) {
      clearInterval(ivRef.current);
      ivRef.current = null;
    }
  }, []);
  useEffect(() => clearIv, [clearIv]);

  const timer: TimerApi = {
    mode: m.current.mode,
    left: m.current.left,
    running: m.current.running,
    workMin: m.current.workMin,
    brkMin: m.current.brkMin,
    linkedTaskId: m.current.linkedTaskId,
    toggle: () => {
      const s = m.current;
      s.running = !s.running;
      if (s.running) {
        if (ivRef.current == null) ivRef.current = window.setInterval(tick, 1000);
      } else {
        clearIv();
        creditActual();
      }
      force();
    },
    reset: () => {
      const s = m.current;
      s.running = false;
      clearIv();
      s.mode = "work";
      s.left = s.workMin * 60;
      s.worked = 0;
      force();
    },
    setPreset: (workMin, brkMin) => {
      const s = m.current;
      s.workMin = workMin;
      s.brkMin = brkMin;
      if (!s.running) {
        s.mode = "work";
        s.left = workMin * 60;
      }
      force();
    },
    setTask: (id) => {
      m.current.linkedTaskId = id;
      force();
    },
  };

  const openFocus = useCallback(
    (id?: string) => {
      const pick =
        (id && tasksRef.current.find((t) => t.id === id)) ||
        tasksRef.current.find((t) => t.status === "doing") ||
        tasksRef.current.find((t) => t.mitDate === today && t.status !== "done") ||
        tasksRef.current
          .filter((t) => t.doDate === today && t.fromHour != null && t.status !== "done")
          .sort((a, b) => (a.fromHour ?? 0) - (b.fromHour ?? 0))[0] ||
        tasksRef.current.filter((t) => t.status !== "done").sort((a, b) => a.pri - b.pri)[0];
      if (!pick) {
        toast("No open tasks to focus on.");
        return;
      }
      m.current.linkedTaskId = pick.id;
      if (!m.current.running) timer.toggle();
      setFocusId(pick.id);
      setFocusOpen(true);
    },
    // timer.toggle is stable enough for this handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today, toast],
  );

  // ---- sync lifecycle ----
  useEffect(() => {
    const run = async () => {
      const changed = await doSync();
      if (changed) {
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["projects"] });
        qc.invalidateQueries({ queryKey: ["dayLogs"] });
      }
    };
    setSyncRunner(run);
    run(); // initial hydrate/pull
    const onOnline = () => run();
    window.addEventListener("online", onOnline);
    const iv = window.setInterval(run, 20_000);
    return () => {
      window.removeEventListener("online", onOnline);
      clearInterval(iv);
    };
  }, [qc]);

  // ---- global keyboard ----
  const gPending = useRef(false);
  const gTimer = useRef<number | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
        return;
      }
      if (e.key === "Escape") {
        setPalette(false);
        setHelp(false);
        setPlan(false);
        setShutdown(false);
        setFocusOpen(false);
        setTaskModal(null);
        return;
      }
      const el = document.activeElement;
      const typing =
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" ||
          el.tagName === "SELECT" ||
          el.tagName === "TEXTAREA" ||
          // Rich-text editors (the notes BlockNote editor) are contenteditable
          // divs, not inputs — without this, letters like "n"/"f" fire global
          // shortcuts instead of being typed into the note.
          el.isContentEditable);
      if (typing) return;

      if (gPending.current) {
        gPending.current = false;
        if (gTimer.current) clearTimeout(gTimer.current);
        const map: Record<string, string> = {
          p: "/app/plan",
          b: "/app/board",
          l: "/app/tasks",
          m: "/app/matrix",
          s: "/app/stats",
        };
        const to = map[e.key];
        if (to) {
          e.preventDefault();
          navigate({ to });
        }
        return;
      }

      switch (e.key) {
        case "g":
          gPending.current = true;
          gTimer.current = window.setTimeout(() => {
            gPending.current = false;
          }, 900);
          break;
        case "?":
          setHelp(true);
          break;
        case "n":
          e.preventDefault();
          openTask(null);
          break;
        case "/":
          e.preventDefault();
          navigate({ to: "/app/plan" });
          setTimeout(() => {
            const input = document.querySelector<HTMLInputElement>(".qadd input");
            input?.focus();
          }, 60);
          break;
        case "f":
          openFocus();
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, openFocus, openTask]);

  const value: AppApi = {
    tasks,
    projects,
    dayLogs,
    notes,
    pmap,
    today,
    todayLog,
    isLoading: tasksQ.isLoading,
    filter,
    setFilter,
    createTask,
    updateTask,
    deleteTask,
    toggleDone,
    toggleMit,
    addProject,
    autoPlaceToday,
    rollStale,
    createNote,
    updateNote,
    deleteNote,
    planDay,
    completeShutdown,
    openPalette: () => setPalette(true),
    openHelp: () => setHelp(true),
    openPlan: () => setPlan(true),
    openShutdown: () => setShutdown(true),
    openFocus,
    openTask,
    toast,
    timer,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <Overlays
        toast={toastState}
        onToastClose={() => setToastState(null)}
        palette={palette}
        onPaletteClose={() => setPalette(false)}
        help={help}
        onHelpClose={() => setHelp(false)}
        plan={plan}
        onPlanClose={() => setPlan(false)}
        shutdown={shutdown}
        onShutdownClose={() => setShutdown(false)}
        focusOpen={focusOpen}
        focusId={focusId}
        onFocusClose={() => setFocusOpen(false)}
        taskModal={taskModal}
        onTaskClose={() => setTaskModal(null)}
      />
    </AppContext.Provider>
  );
}
